import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Box,
  LinearProgress,
  Typography,
  InputAdornment,
  Grid,
  SelectChangeEvent,
} from "@mui/material";
import {
  AddCircle as AddCircleIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import axios from "axios";
import UserCreateDialog from "../../components/dialogs/UserCreateDialog";
import UserAuditLogDialog from "../../components/dialogs/UserAuditLogDialog";
import Pagination from "../../components/common/Pagination";
import { formatUserForDisplay } from "../../utils/userFormatting";

// Types
type Bank = {
  _id: string;
  name: string;
};

type AgencyDepartment = {
  _id: string;
  title: string;
};

type GeneralInfo = {
  banks: Bank[];
  agency_departments: AgencyDepartment[];
};

type User = {
  _id: string;
  name: string;
  email: string;
  permissions: string[];
  [key: string]: any;
};

const TABLE_HEADERS = [
  { id: "icon", label: "", align: "center" },
  { id: "bankDisplay", label: "ფინანსური ინსტიტუცია", minWidth: 100, align: "center" },
  { id: "name", label: "სახელი", minWidth: 150, align: "center" },
  { id: "email", label: "ელ-ფოსტა", minWidth: 100, align: "center" },
  { id: "actions", label: "", minWidth: 50, align: "center" },
];

function Users() {
  // State
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedBankId, setSelectedBankId] = useState("");
  
  // Dialogs State
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [auditLogDialogOpen, setAuditLogDialogOpen] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<User | null>(null);
  const [selectedUserForAudit, setSelectedUserForAudit] = useState<User | null>(null);

  // General Data & Permissions
  const [generalFilterData, setGeneralFilterData] = useState<GeneralInfo>({ banks: [], agency_departments: [] });
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [isBank, setIsBank] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [isMonitoringCGS, setIsMonitoringCGS] = useState(false);
  const [isMonitoringReview, setIsMonitoringReview] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Initialize Roles
  useEffect(() => {
    const role = localStorage.getItem("role");
    if (role === "ROLE_BANK") setIsBank(true);
    if (role === "ROLE_MONITORING") setIsMonitoring(true);
    if (role === "ROLE_MONITORING_CGS") setIsMonitoringCGS(true);
    if (role === "ROLE_MONITORING_REVIEW") setIsMonitoringReview(true);
    if (localStorage.getItem("isAdmin")) setIsAdmin(true);
    
    const perms = localStorage.getItem("permissions");
    if (perms) {
      // Handle potential string vs array format in localStorage
      try {
        setUserPermissions(Array.isArray(perms) ? perms : (perms.includes('[') ? JSON.parse(perms) : perms.split(',')));
      } catch (e) {
        setUserPermissions([perms]);
      }
    }
  }, []);

  // Fetch General Info
  useEffect(() => {
    axios
      .get("/general_info")
      .then((resp) => {
        setGeneralFilterData(resp.data);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  // Debounce Search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to first page on search
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch Users
  const fetchUsers = useCallback(() => {
    setLoading(true);
    const filterData = {
      page: page - 1, // API expects 0-based
      limit: rowsPerPage,
      search: debouncedSearch,
      bankId: selectedBankId || undefined,
    };

    axios
      .post("/fetch_users", filterData)
      .then((res) => {
        // Handle both old array response and new object response for safety, though we expect object now
        const responseData = res.data.users || res.data; 
        const totalCount = res.data.total || 0;
        
        const updatedUsers = (Array.isArray(responseData) ? responseData : []).map((result: any) => {
          let formattedResult = formatUserForDisplay(result);
          
          if (formattedResult.permissions && formattedResult.permissions.includes("ADD_USER")) {
            formattedResult = { 
              ...formattedResult, 
              icon: <Tooltip title="ადმინისტრატორი"><SupervisorAccountIcon color="primary" /></Tooltip> 
            };
          }
          return formattedResult;
        });

        setUsers(updatedUsers);
        setTotal(res.data.total !== undefined ? res.data.total : updatedUsers.length); // Fallback if total not provided
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [page, rowsPerPage, debouncedSearch, selectedBankId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(event.target.value as number);
    setPage(1);
  };

  const showBankFilter = !isBank && !isMonitoring && !isMonitoringCGS && !isMonitoringReview;
  const canAddUser = userPermissions.includes("ADD_USER");

  return (
    <Box sx={{ p: 3 }}>
      {/* Dialogs */}
      <UserCreateDialog
        open={createUserDialogOpen}
        setOpen={setCreateUserDialogOpen}
        banks={generalFilterData?.banks}
        isBank={isBank}
        isMonitoring={isMonitoring}
        isMonitoringCGS={isMonitoringCGS}
        isMonitoringReview={isMonitoringReview}
        agencyDepartments={generalFilterData?.agency_departments}
        onUserUpdated={fetchUsers}
      />
      <UserCreateDialog
        open={editUserDialogOpen}
        setOpen={setEditUserDialogOpen}
        banks={generalFilterData?.banks}
        isBank={isBank}
        isMonitoring={isMonitoring}
        isMonitoringCGS={isMonitoringCGS}
        isMonitoringReview={isMonitoringReview}
        agencyDepartments={generalFilterData?.agency_departments}
        editUser={selectedUserForEdit}
        onUserUpdated={() => {
          fetchUsers();
          setSelectedUserForEdit(null);
        }}
      />
      <UserAuditLogDialog
        open={auditLogDialogOpen}
        onClose={() => {
          setAuditLogDialogOpen(false);
          setSelectedUserForAudit(null);
        }}
        userId={selectedUserForAudit?._id || ""}
        userName={selectedUserForAudit?.name || ""}
      />

      <Card variant="outlined">
        <CardHeader
          title="მომხმარებლები"
          titleTypographyProps={{ variant: "h5", fontWeight: 600 }}
          action={
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              {showBankFilter && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="bank-label">ინსტიტუცია</InputLabel>
                  <Select
                    labelId="bank-label"
                    value={selectedBankId}
                    label="ინსტიტუცია"
                    onChange={(event) => {
                      setSelectedBankId(event.target.value);
                      setPage(1);
                    }}
                  >
                    <MenuItem value="">
                      <em>ყველა</em>
                    </MenuItem>
                    <MenuItem value="agency">სააგენტო</MenuItem>
                    <MenuItem value="monitoring">მონიტორინგი</MenuItem>
                    <MenuItem value="monitoring_micro">მონიტორინგი (მიკრო)</MenuItem>
                    {generalFilterData?.banks?.map((bank) => (
                      <MenuItem key={bank._id} value={bank._id}>
                        {bank.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {canAddUser && (
                <>
                  <TextField
                    placeholder="ძებნა (სახელი, ელ-ფოსტა)..."
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ minWidth: 250 }}
                  />
                  <Button
                    onClick={() => setCreateUserDialogOpen(true)}
                    variant="contained"
                    color="primary"
                    startIcon={<AddCircleIcon />}
                    disableElevation
                  >
                    მომხმარებლის დამატება
                  </Button>
                </>
              )}
            </Box>
          }
        />
      </Card>

      <Paper variant="outlined" sx={{ mt: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
          <Table stickyHeader aria-label="users table">
            <TableHead>
              <TableRow>
                {TABLE_HEADERS.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align as any}
                    style={{ minWidth: column.minWidth, fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                 <TableRow>
                  <TableCell colSpan={5} sx={{ p: 0 }}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                 <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography color="textSecondary">მომხმარებლები არ მოიძებნა</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((row) => (
                  <TableRow hover role="checkbox" tabIndex={-1} key={row._id}>
                    <TableCell align="center">{row.icon}</TableCell>
                    <TableCell align="center">{row.bankDisplay}</TableCell>
                    <TableCell align="center">{row.name}</TableCell>
                    <TableCell align="center">{row.email}</TableCell>
                    <TableCell align="center">
                      {(isAdmin ||
                        (canAddUser && !isBank) ||
                        (canAddUser && isBank && !row.permissions.includes("ADD_USER"))) && (
                        <Box display="flex" justifyContent="center">
                          <Tooltip title="რედაქტირება">
                            <IconButton
                              onClick={() => {
                                setSelectedUserForEdit(row);
                                setEditUserDialogOpen(true);
                              }}
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="ცვლილებების ისტორია">
                            <IconButton
                              onClick={() => {
                                setSelectedUserForAudit(row);
                                setAuditLogDialogOpen(true);
                              }}
                              color="primary"
                              size="small"
                            >
                              <HistoryIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination
          count={Math.ceil(total / rowsPerPage)}
          page={page}
          onChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}

export default Users;
